import { useState, useEffect } from 'react';
import MergeChatLogo from '../components/MergeChatLogo';
import { useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';

const OA_COLORS = [
  '#185FA5', '#3B6D11', '#854F0B', '#993556',
  '#534AB7', '#0F6E56', '#B91C1C', '#0369A1',
  '#7C3AED', '#C2410C',
];

function OAForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    name: '', channel_id: '', channel_secret: '', access_token: '', color: '#185FA5',
    auto_reply_enabled: false, auto_reply_message: '', website_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!initial?.id;

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('กรุณาใส่ชื่อ OA');
    if (!isEdit && !form.channel_id.trim()) return setError('กรุณาใส่ Channel ID');
    if (!isEdit && !form.channel_secret.trim()) return setError('กรุณาใส่ Channel Secret');
    if (!isEdit && !form.access_token.trim()) return setError('กรุณาใส่ Access Token');
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'แก้ไข OA' : 'เพิ่ม OA ใหม่'}</h3>
          <button className="btn-icon" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label>ชื่อ OA <span className="required">*</span></label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="เช่น Shop A, ฝ่ายบริการ" />
          </div>

          {!isEdit && <>
            <div className="form-field">
              <label>Channel ID <span className="required">*</span></label>
              <input value={form.channel_id} onChange={e => set('channel_id', e.target.value)}
                placeholder="เช่น 2008926200" />
            </div>
            <div className="form-field">
              <label>Channel Secret <span className="required">*</span></label>
              <input value={form.channel_secret} onChange={e => set('channel_secret', e.target.value)}
                placeholder="32-character secret" type="password" />
            </div>
            <div className="form-field">
              <label>Channel Access Token <span className="required">*</span></label>
              <textarea value={form.access_token} onChange={e => set('access_token', e.target.value)}
                placeholder="Long-lived access token จาก Line Developers Console"
                rows={3} />
            </div>
          </>}

          {isEdit && <>
            <div className="form-field">
              <label>Channel Secret ใหม่ <span className="optional">(เว้นว่างถ้าไม่เปลี่ยน)</span></label>
              <input value={form.channel_secret || ''} onChange={e => set('channel_secret', e.target.value)}
                placeholder="32-character secret" type="password" />
            </div>
            <div className="form-field">
              <label>Access Token ใหม่ <span className="optional">(เว้นว่างถ้าไม่เปลี่ยน)</span></label>
              <textarea value={form.access_token || ''} onChange={e => set('access_token', e.target.value)}
                placeholder="Long-lived access token ใหม่"
                rows={3} />
            </div>
          </>}

          <div className="form-field">
            <label>สีประจำ OA</label>
            <div className="color-picker">
              {OA_COLORS.map(c => (
                <button key={c} type="button"
                  className={`color-swatch ${form.color === c ? 'selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => set('color', c)}
                />
              ))}
              <input type="color" value={form.color}
                onChange={e => set('color', e.target.value)}
                title="เลือกสีเอง" className="color-custom" />
            </div>
            <div className="color-preview">
              <span className="oa-tag-preview" style={{ background: form.color + '22', color: form.color }}>
                {form.name || 'ชื่อ OA'}
              </span>
            </div>
          </div>

          {/* Website URL */}
          <div className="form-field">
            <label>เว็บไซต์ <span className="optional">(URL)</span></label>
            <input
              value={form.website_url || ''}
              onChange={e => set('website_url', e.target.value)}
              placeholder="https://www.example.com"
              type="url"
            />
          </div>

          {/* Auto-reply section */}
          <div className="form-field auto-reply-section">
            <div className="auto-reply-header">
              <div>
                <label style={{ display: 'inline', marginBottom: 0 }}>🤖 Auto-reply ทักทายลูกค้า</label>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
                  ส่งข้อความต้อนรับอัตโนมัติเมื่อลูกค้าทักข้อความแรกของวัน (00:00 น. ไทย)
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={!!form.auto_reply_enabled}
                  onChange={e => set('auto_reply_enabled', e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>
            {form.auto_reply_enabled && (
              <textarea
                className="auto-reply-textarea"
                placeholder={`เช่น สวัสดีครับ! ขอบคุณที่ติดต่อ ${form.name || 'เรา'}\nเจ้าหน้าที่จะตอบกลับโดยเร็วที่สุดครับ 🙏`}
                value={form.auto_reply_message || ''}
                onChange={e => set('auto_reply_message', e.target.value)}
                rows={3}
              />
            )}
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>ยกเลิก</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'กำลังบันทึก...' : isEdit ? 'บันทึกการแก้ไข' : 'เพิ่ม OA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OACard({ oa, onEdit, onDelete, onToggle, onBroadcast, onRichMenu }) {
  const [deleting, setDeleting] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (!oa.is_active) return;
    api.getOaQuota(oa.id).then(setQuota).catch(() => setQuota(null));
  }, [oa.id, oa.is_active]);

  async function handleDelete() {
    if (!window.confirm(`ลบ "${oa.name}" ออกจากระบบ?\n\nประวัติแชททั้งหมดจะถูกลบด้วย`)) return;
    setDeleting(true);
    try { await onDelete(oa.id); }
    finally { setDeleting(false); }
  }

  const quotaTotal  = Number(quota?.quota?.value) || 0;
  const quotaUsed   = Number(quota?.consumption?.totalUsage) || 0;
  const quotaRemain = Math.max(0, quotaTotal - quotaUsed);
  const quotaPct    = quotaTotal ? Math.round((quotaUsed / quotaTotal) * 100) : 0;
  const quotaWarn   = quotaPct >= 80;

  return (
    <div className={`oa-card ${!oa.is_active ? 'inactive' : ''}`}>
      <div className="oa-card-left">
        <div className="oa-color-badge" style={{ background: oa.color }} />
        <div>
          <div className="oa-card-name">{oa.name}</div>
          <div className="oa-card-id">Channel ID: {oa.channel_id}</div>
          <div className="oa-card-meta">
            เพิ่มเมื่อ {new Date(oa.created_at).toLocaleDateString('th-TH')}
          </div>
          {quota && oa.is_active && (
            <div className={`oa-quota ${quotaWarn ? 'warn' : ''}`}>
              📊 Quota: <strong>{quotaUsed.toLocaleString()}</strong> / {quotaTotal.toLocaleString()}
              <span className="oa-quota-remain"> (เหลือ {quotaRemain.toLocaleString()})</span>
              <div className="oa-quota-bar"><div className="oa-quota-fill" style={{ width: `${Math.min(100, quotaPct)}%` }} /></div>
            </div>
          )}
        </div>
      </div>
      <div className="oa-card-right">
        <div className="toggle-wrap">
          <span className={`status-pill ${oa.is_active ? 'active' : 'inactive'}`}>
            {oa.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </span>
          <button className="btn-toggle" onClick={() => onToggle(oa)}
            title={oa.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}>
            {oa.is_active ? '⏸' : '▶'}
          </button>
        </div>
        <div className="oa-card-actions">
          <button className="btn-edit" onClick={() => onBroadcast(oa)} title="Broadcast">📢</button>
          <button className="btn-edit" onClick={() => onRichMenu(oa)} title="Rich Menu">🍔</button>
          <button className="btn-edit" onClick={() => onEdit(oa)}>✎ แก้ไข</button>
          <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? '...' : '🗑 ลบ'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── V2.8: Broadcast Modal ────────────────────────────
function BroadcastModal({ oa, onClose }) {
  const [type, setType]       = useState('text');
  const [text, setText]       = useState('');
  const [altText, setAltText] = useState('');
  const [flexJson, setFlexJson] = useState('');
  const [quota, setQuota]     = useState(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus]   = useState('');

  useEffect(() => { api.getOaQuota(oa.id).then(setQuota).catch(() => {}); }, [oa.id]);

  const remain = quota ? (Number(quota.quota?.value) || 0) - (Number(quota.consumption?.totalUsage) || 0) : null;

  async function send() {
    if (!window.confirm(`ส่ง broadcast ให้ follower ทุกคนของ "${oa.name}"?\n\n— ตรวจสอบให้ดีก่อนกด OK!`)) return;
    setSending(true); setStatus('');
    try {
      if (type === 'text') {
        if (!text.trim()) throw new Error('กรุณากรอกข้อความ');
        await api.broadcastMessage(oa.id, { type: 'text', text: text.trim() });
      } else {
        if (!altText.trim()) throw new Error('กรุณากรอก altText');
        let contents;
        try { contents = JSON.parse(flexJson); }
        catch { throw new Error('JSON ไม่ถูกต้อง'); }
        await api.broadcastMessage(oa.id, { type: 'flex', altText: altText.trim(), contents });
      }
      setStatus('✅ ส่ง broadcast เรียบร้อย');
      api.getOaQuota(oa.id).then(setQuota);
      setTimeout(onClose, 1500);
    } catch (err) {
      setStatus('❌ ' + err.message);
    } finally { setSending(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h3>📢 Broadcast — {oa.name}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 20px 20px' }}>
          {quota && (
            <div className="dash-card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13 }}>
                📊 Quota: <strong>{Number(quota.consumption?.totalUsage || 0).toLocaleString()}</strong> / {Number(quota.quota?.value || 0).toLocaleString()}
                <span style={{ marginLeft: 8, color: remain > 100 ? 'var(--accent)' : 'var(--danger)' }}>
                  (เหลือ {remain?.toLocaleString()} ข้อความ)
                </span>
              </div>
            </div>
          )}

          <div className="form-field">
            <label>ประเภท</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <label><input type="radio" checked={type==='text'} onChange={() => setType('text')} /> Text</label>
              <label><input type="radio" checked={type==='flex'} onChange={() => setType('flex')} /> Flex Message</label>
            </div>
          </div>

          {type === 'text' ? (
            <div className="form-field">
              <label>ข้อความที่จะ broadcast</label>
              <textarea className="form-input" rows={5} value={text}
                onChange={e => setText(e.target.value)}
                placeholder="เช่น โปรโมชั่นพิเศษวันนี้..."
                style={{ resize: 'vertical' }} />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                {text.length} ตัวอักษร — จะส่งให้ทุก follower ของ OA นี้
              </div>
            </div>
          ) : (
            <>
              <div className="form-field">
                <label>Alt Text (โผล่ใน notification)</label>
                <input className="form-input" value={altText} onChange={e => setAltText(e.target.value)}
                  placeholder="เช่น โปรโมชั่นพิเศษวันนี้" />
              </div>
              <div className="form-field">
                <label>Flex JSON (contents object)</label>
                <textarea className="form-input" rows={8} value={flexJson}
                  onChange={e => setFlexJson(e.target.value)}
                  placeholder={'{\n  "type": "bubble",\n  "body": { ... }\n}'}
                  style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  ใช้ <a href="https://developers.line.biz/flex-simulator/" target="_blank" rel="noreferrer">LINE Flex Simulator</a> ออกแบบแล้ว copy contents object มาวาง
                </div>
              </div>
            </>
          )}

          {status && <div style={{ marginTop: 8, fontSize: 13, color: status.startsWith('❌') ? 'var(--danger)' : 'var(--accent)' }}>{status}</div>}

          <div className="form-actions" style={{ marginTop: 14 }}>
            <button className="btn-secondary" onClick={onClose}>ยกเลิก</button>
            <button className="btn-primary" onClick={send} disabled={sending || (remain != null && remain < 1)}>
              {sending ? 'กำลังส่ง...' : '📢 Broadcast'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── V2.8: Rich Menu Modal ────────────────────────────
function RichMenuModal({ oa, onClose }) {
  const [menus, setMenus]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [json, setJson]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState('');

  function reload() {
    setLoading(true);
    api.getRichMenus(oa.id).then(r => setMenus(r.richmenus || [])).catch(console.error).finally(() => setLoading(false));
  }
  useEffect(reload, [oa.id]); // eslint-disable-line

  async function handleDelete(rmId) {
    if (!window.confirm('ลบ rich menu นี้?')) return;
    try { await api.deleteRichMenu(oa.id, rmId); reload(); }
    catch (err) { alert(err.message); }
  }

  async function handleSetDefault(rmId) {
    try { await api.setDefaultRichMenu(oa.id, rmId); setStatus('✅ ตั้งเป็น default แล้ว'); setTimeout(() => setStatus(''), 2000); }
    catch (err) {
      alert(err.message + '\n\n💡 ถ้า error 400 — อาจยังไม่ได้ upload image (LINE ไม่ยอมตั้ง default ถ้า rich menu ไม่มีรูป)');
    }
  }

  async function handleUploadImage(rmId, file) {
    if (!file) return;
    if (file.size > 1024 * 1024) { alert('Image ต้อง ≤ 1MB'); return; }
    setStatus('⏳ กำลัง upload...');
    try {
      await api.uploadRichMenuImage(oa.id, rmId, file);
      setStatus('✅ Upload image แล้ว — ตั้ง default ได้แล้ว');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) { setStatus('❌ ' + err.message); }
  }

  async function handleCreate() {
    setSaving(true); setStatus('');
    try {
      let menu;
      try { menu = JSON.parse(json); }
      catch { throw new Error('JSON ไม่ถูกต้อง'); }
      await api.createRichMenu(oa.id, menu);
      setStatus('✅ สร้างแล้ว — อย่าลืม upload image แยกผ่าน LINE API');
      setShowForm(false); setJson(''); reload();
    } catch (err) {
      setStatus('❌ ' + err.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3>🍔 Rich Menu — {oa.name}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{menus.length} รายการ</span>
            <button className="btn-secondary" onClick={() => setShowForm(!showForm)}>
              {showForm ? '✕ ยกเลิก' : '+ สร้างใหม่'}
            </button>
          </div>

          {showForm && (
            <div className="dash-card" style={{ marginBottom: 12 }}>
              <div className="form-field">
                <label>Rich Menu JSON</label>
                <textarea className="form-input" rows={10} value={json}
                  onChange={e => setJson(e.target.value)}
                  placeholder={'{\n  "size": { "width": 2500, "height": 1686 },\n  "selected": false,\n  "name": "Menu 1",\n  "chatBarText": "เมนู",\n  "areas": [\n    { "bounds": { "x": 0, "y": 0, "width": 1250, "height": 1686 },\n      "action": { "type": "message", "text": "เช็คยอด" } }\n  ]\n}'}
                  style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
              </div>
              <button className="btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'กำลังสร้าง...' : 'สร้าง Rich Menu'}
              </button>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                💡 หลังสร้างต้อง upload image แยก (PNG 2500×1686 สำหรับ full / 2500×843 สำหรับ half) ผ่าน LINE Manager หรือ API
              </div>
            </div>
          )}

          {status && <div style={{ marginBottom: 10, fontSize: 13, color: status.startsWith('❌') ? 'var(--danger)' : 'var(--accent)' }}>{status}</div>}

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>กำลังโหลด...</div>
          ) : menus.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              ยังไม่มี rich menu — กด "สร้างใหม่" เพื่อเริ่ม
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {menus.map(rm => (
                <div key={rm.richMenuId} className="dash-card" style={{ padding: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{rm.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace', marginTop: 2 }}>
                    {rm.richMenuId}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Chat bar: "{rm.chatBarText}" — Size: {rm.size?.width}×{rm.size?.height} — Areas: {rm.areas?.length || 0}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label className="btn-secondary-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                      📷 Upload image
                      <input type="file" accept="image/png,image/jpeg" style={{ display: 'none' }}
                        onChange={e => { handleUploadImage(rm.richMenuId, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                    <button className="btn-secondary-sm" onClick={() => handleSetDefault(rm.richMenuId)}>⭐ ตั้ง default</button>
                    <button className="btn-secondary-sm" onClick={() => handleDelete(rm.richMenuId)} style={{ color: 'var(--danger)' }}>🗑 ลบ</button>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    💡 Upload image ก่อน → ตั้ง default → user เห็นเมนูใน LINE
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Quick Replies Tab ────────────────────────────────
function QuickRepliesTab() {
  const [rows, setRows] = useState(
    Array.from({ length: 9 }, (_, i) => ({ key_number: i + 1, label: '', content: '' }))
  );
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.getQuickReplies().then(data => {
      setRows(prev => prev.map(r => {
        const found = data.find(d => d.key_number === r.key_number);
        return found || r;
      }));
    });
  }, []);

  function update(keyNum, field, val) {
    setRows(prev => prev.map(r => r.key_number === keyNum ? { ...r, [field]: val } : r));
  }

  async function save(r) {
    if (!r.label.trim() || !r.content.trim()) return;
    setSaving(r.key_number);
    try {
      await api.saveQuickReply(r.key_number, { label: r.label, content: r.content });
    } catch (err) { alert(err.message); }
    finally { setSaving(null); }
  }

  async function remove(r) {
    setSaving(r.key_number);
    try {
      await api.deleteQuickReply(r.key_number);
      setRows(prev => prev.map(x => x.key_number === r.key_number ? { key_number: r.key_number, label: '', content: '' } : x));
    } catch (err) { alert(err.message); }
    finally { setSaving(null); }
  }

  return (
    <>
      <div className="settings-header">
        <div>
          <h1>Quick Replies</h1>
          <p>ตั้งข้อความสำเร็จรูป กด Alt+1~9 เพื่อใช้งานในหน้า Inbox</p>
        </div>
      </div>
      <div className="qr-settings-list">
        {rows.map(r => (
          <div key={r.key_number} className="qr-settings-row">
            <div className="qr-key-badge">Alt+{r.key_number}</div>
            <div className="qr-fields">
              <input
                className="qr-label-input"
                placeholder="ชื่อสั้น เช่น ขอบคุณ"
                value={r.label}
                onChange={e => update(r.key_number, 'label', e.target.value)}
              />
              <textarea
                className="qr-content-input"
                placeholder="ข้อความเต็มที่จะส่ง..."
                value={r.content}
                rows={2}
                onChange={e => update(r.key_number, 'content', e.target.value)}
              />
            </div>
            <div className="qr-actions">
              <button
                className="btn-primary qr-save-btn"
                onClick={() => save(r)}
                disabled={saving === r.key_number || !r.label.trim() || !r.content.trim()}
              >
                {saving === r.key_number ? '...' : 'บันทึก'}
              </button>
              {(r.label || r.content) && (
                <button className="btn-delete" onClick={() => remove(r)} disabled={saving === r.key_number}>
                  ลบ
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Tags Tab ─────────────────────────────────────────
const TAG_PRESET_COLORS = ['#16a34a','#FF6347','#5B8DEF','#FFB347','#00D9A6','#9333ea','#db2777','#0891b2','#65a30d','#b45309'];

function TagsTab() {
  const [tags, setTags] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#16a34a');
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  useEffect(() => { api.getTags().then(setTags); }, []);

  async function handleAdd() {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const tag = await api.createTag({ name: newName.trim(), color: newColor });
      setTags(prev => [...prev, tag]);
      setNewName('');
    } catch (err) { alert(err.message); }
    finally { setAdding(false); }
  }

  async function handleSaveEdit(id) {
    try {
      const updated = await api.updateTag(id, { name: editName, color: editColor });
      setTags(prev => prev.map(t => t.id === id ? updated : t));
      setEditId(null);
    } catch (err) { alert(err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('ลบ Tag นี้? ข้อมูลที่ Tag ไว้จะหายด้วย')) return;
    try {
      await api.deleteTag(id);
      setTags(prev => prev.filter(t => t.id !== id));
    } catch (err) { alert(err.message); }
  }

  return (
    <>
      <div className="settings-header">
        <div>
          <h1>จัดการ Tags</h1>
          <p>Tag ลูกค้าแต่ละแชทได้ เช่น ลูกค้าดีเด่น, ลูกค้าเก่า</p>
        </div>
      </div>

      {/* Add new tag */}
      <div className="tag-add-form">
        <input
          className="tag-name-input"
          placeholder="ชื่อ Tag เช่น ลูกค้าดีเด่น"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <div className="tag-color-row">
          {TAG_PRESET_COLORS.map(c => (
            <button key={c} className={`tag-color-btn ${newColor === c ? 'selected' : ''}`}
              style={{ background: c }} onClick={() => setNewColor(c)} />
          ))}
          <input type="color" className="tag-color-custom" value={newColor}
            onChange={e => setNewColor(e.target.value)} title="เลือกสีเอง" />
        </div>
        <button className="btn-primary" onClick={handleAdd} disabled={adding || !newName.trim()}>
          {adding ? '...' : '+ เพิ่ม Tag'}
        </button>
      </div>

      {/* Tag list */}
      <div className="tag-settings-list">
        {tags.length === 0 && <div className="empty-state-settings"><p>ยังไม่มี Tag</p></div>}
        {tags.map(tag => (
          <div key={tag.id} className="tag-settings-row">
            {editId === tag.id ? (
              <>
                <input className="tag-name-input" value={editName} onChange={e => setEditName(e.target.value)} />
                <div className="tag-color-row">
                  {TAG_PRESET_COLORS.map(c => (
                    <button key={c} className={`tag-color-btn ${editColor === c ? 'selected' : ''}`}
                      style={{ background: c }} onClick={() => setEditColor(c)} />
                  ))}
                  <input type="color" className="tag-color-custom" value={editColor}
                    onChange={e => setEditColor(e.target.value)} />
                </div>
                <div className="tag-row-actions">
                  <button className="btn-primary" onClick={() => handleSaveEdit(tag.id)}>บันทึก</button>
                  <button className="btn-secondary" onClick={() => setEditId(null)}>ยกเลิก</button>
                </div>
              </>
            ) : (
              <>
                <span className="tag-pill" style={{ background: tag.color + '22', color: tag.color, borderColor: tag.color + '55' }}>
                  {tag.name}
                </span>
                <div className="tag-row-actions">
                  <button className="btn-edit" onClick={() => { setEditId(tag.id); setEditName(tag.name); setEditColor(tag.color); }}>✎ แก้ไข</button>
                  <button className="btn-delete" onClick={() => handleDelete(tag.id)}>ลบ</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Agents Tab (admin only) ──────────────────────────
function AgentsTab() {
  const { agent: me } = useAuth();
  const [agents, setAgents]     = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // agent object or null=create
  const [form, setForm]         = useState({ name: '', email: '', password: '', role: 'agent' });
  const [saving, setSaving]     = useState(false);

  useEffect(() => { api.getAgents().then(setAgents).catch(() => {}); }, []);

  function openCreate() { setForm({ name: '', email: '', password: '', role: 'agent' }); setEditTarget(null); setShowForm(true); }
  function openEdit(a)  { setForm({ name: a.name, email: a.email, password: '', role: a.role }); setEditTarget(a); setShowForm(true); }
  function closeForm()  { setShowForm(false); setEditTarget(null); }

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim()) return alert('กรอกชื่อและ Username / ID');
    if (!editTarget && !form.password.trim()) return alert('กรอกรหัสผ่าน');
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password.trim()) payload.password = form.password;
      if (editTarget) {
        const updated = await api.updateAgent(editTarget.id, payload);
        setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
      } else {
        const created = await api.createAgent(payload);
        setAgents(prev => [...prev, created]);
      }
      closeForm();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(a) {
    if (!window.confirm(`ลบ Agent "${a.name}" ใช่ไหม?`)) return;
    try {
      await api.deleteAgent(a.id);
      setAgents(prev => prev.filter(x => x.id !== a.id));
    } catch (err) { alert(err.message); }
  }

  return (
    <div className="tab-section">
      <div className="section-header-row">
        <div>
          <div className="section-title">👥 จัดการ Agent</div>
          <div className="section-desc">สร้าง แก้ไข และลบบัญชี Agent</div>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ สร้าง Agent</button>
      </div>

      <table className="agent-mgmt-table">
        <thead>
          <tr><th>ชื่อ</th><th>Username / ID</th><th>Role</th><th>สร้างเมื่อ</th><th></th></tr>
        </thead>
        <tbody>
          {agents.map(a => (
            <tr key={a.id} className={a.id === me?.id ? 'agent-row-self' : ''}>
              <td><strong>{a.name}</strong> {a.id === me?.id && <span className="self-badge">คุณ</span>}</td>
              <td className="text-muted">{a.email}</td>
              <td><span className={`role-badge role-${a.role}`}>{a.role}</span></td>
              <td className="text-muted">{a.created_at ? new Date(a.created_at).toLocaleDateString('th-TH') : '—'}</td>
              <td className="agent-actions">
                <button className="btn-sm" onClick={() => openEdit(a)}>แก้ไข</button>
                {a.id !== me?.id && <button className="btn-sm btn-danger" onClick={() => handleDelete(a)}>ลบ</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <span>{editTarget ? 'แก้ไข Agent' : 'สร้าง Agent ใหม่'}</span>
              <button className="modal-close" onClick={closeForm}>✕</button>
            </div>
            <div className="modal-body">
              {[['ชื่อ', 'name', 'text', 'ชื่อ Agent...'], ['Username / ID', 'email', 'text', 'username หรือ email'],
                ['รหัสผ่าน', 'password', 'password', editTarget ? 'เว้นว่างถ้าไม่ต้องการเปลี่ยน' : 'รหัสผ่าน...']
              ].map(([label, key, type, ph]) => (
                <div className="form-field" key={key}>
                  <label>{label}</label>
                  <input type={type} placeholder={ph} value={form[key]}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} />
                </div>
              ))}
              <div className="form-field">
                <label>Role</label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary-sm" onClick={closeForm}>ยกเลิก</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'กำลังบันทึก...' : editTarget ? 'บันทึก' : 'สร้าง Agent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OA Permissions Tab (admin only) ─────────────────
function PermissionsTab() {
  const [agents, setAgents] = useState([]);
  const [oaList, setOaList] = useState([]);
  const [perms, setPerms] = useState({}); // { agentId: Set<oaId> }
  const [saving, setSaving] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.getAgents(), api.getOAAccounts()])
      .then(async ([ags, oas]) => {
        const nonAdmins = ags.filter(a => a.role !== 'admin');
        setAgents(nonAdmins);
        setOaList(oas);
        // Load permissions for each agent
        const permMap = {};
        await Promise.all(nonAdmins.map(async a => {
          const ids = await api.getAgentOaPermissions(a.id);
          permMap[a.id] = new Set(ids);
        }));
        setPerms(permMap);
        setLoaded(true);
      })
      .catch(console.error);
  }, []);

  function toggle(agentId, oaId) {
    setPerms(prev => {
      const s = new Set(prev[agentId] || []);
      if (s.has(oaId)) s.delete(oaId); else s.add(oaId);
      return { ...prev, [agentId]: s };
    });
  }

  async function save(agentId) {
    setSaving(agentId);
    try {
      const oaIds = [...(perms[agentId] || [])];
      await api.setAgentOaPermissions(agentId, oaIds);
    } catch (err) { alert(err.message); }
    finally { setSaving(null); }
  }

  if (!loaded) return <div className="loading-state">กำลังโหลด...</div>;

  return (
    <>
      <div className="settings-header">
        <div>
          <h1>จัดการสิทธิ์การมองเห็น</h1>
          <p>กำหนดว่า Agent แต่ละคนมองเห็น Line OA ไหนได้บ้าง (ไม่เลือก = เห็นทั้งหมด)</p>
        </div>
      </div>
      {agents.length === 0 && (
        <div className="empty-state-settings"><p>ไม่มี Agent ที่ต้องกำหนดสิทธิ์</p></div>
      )}
      <div className="perm-list">
        {agents.map(agent => (
          <div key={agent.id} className="perm-card">
            <div className="perm-agent-header">
              <div className="agent-avatar" style={{ width: 36, height: 36, fontSize: 13 }}>
                {agent.name.slice(0, 2)}
              </div>
              <div>
                <div className="perm-agent-name">{agent.name}</div>
                <div className="perm-agent-email">{agent.email}</div>
              </div>
              <button
                className="btn-primary perm-save-btn"
                onClick={() => save(agent.id)}
                disabled={saving === agent.id}
              >
                {saving === agent.id ? '...' : 'บันทึก'}
              </button>
            </div>
            <div className="perm-oa-grid">
              {oaList.map(oa => {
                const checked = (perms[agent.id] || new Set()).has(oa.id);
                return (
                  <label key={oa.id} className={`perm-oa-item ${checked ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(agent.id, oa.id)}
                      style={{ display: 'none' }}
                    />
                    <span className="perm-oa-dot" style={{ background: oa.color }} />
                    <span className="perm-oa-name">{oa.name}</span>
                    <span className="perm-oa-check">{checked ? '✓' : ''}</span>
                  </label>
                );
              })}
            </div>
            {(perms[agent.id] || new Set()).size === 0 && (
              <div className="perm-note">ไม่ได้เลือก = เห็นทุก OA</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Bot Tab (Shadow Mode) ────────────────────────────
const BOT_MODES = [
  { value: 'off',    label: '⛔ ปิด' },
  { value: 'shadow', label: '👁 Shadow (ทดสอบ)' },
  { value: 'draft',  label: '📝 Draft (coming soon)', disabled: true },
  { value: 'auto',   label: '🤖 Auto (coming soon)',  disabled: true },
];

function BotTab({ oaList }) {
  const [selectedOa, setSelectedOa] = useState(oaList[0]?.id || '');
  const [faqs, setFaqs]         = useState([]);
  const [stats, setStats]       = useState(null);
  const [botMode, setBotMode]   = useState('off');
  const [botName, setBotName]   = useState('AI Assistant');
  const [newQ, setNewQ]         = useState('');
  const [newA, setNewA]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [adding, setAdding]     = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [summary, setSummary]   = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [daysSelected, setDaysSelected] = useState(7);

  useEffect(() => {
    if (!selectedOa) return;
    const oa = oaList.find(o => o.id === Number(selectedOa));
    if (oa) { setBotMode(oa.bot_mode || 'off'); setBotName(oa.bot_name || 'AI Assistant'); }
    api.getBotFaqs(selectedOa).then(setFaqs).catch(console.error);
    setStatsLoading(true);
    setSummary(null);
    api.getBotStats(selectedOa, daysSelected).then(setStats).catch(console.error).finally(() => setStatsLoading(false));
  }, [selectedOa, daysSelected]); // eslint-disable-line

  async function saveSettings() {
    setSaving(true);
    try {
      await api.updateOAAccount(selectedOa, { bot_mode: botMode, bot_name: botName });
      alert('บันทึกแล้ว');
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function addFaq() {
    if (!newQ.trim() || !newA.trim()) return;
    setAdding(true);
    try {
      const faq = await api.createBotFaq(selectedOa, { question: newQ.trim(), answer: newA.trim() });
      setFaqs(prev => [...prev, faq]);
      setNewQ(''); setNewA('');
    } catch (err) { alert(err.message); }
    finally { setAdding(false); }
  }

  async function deleteFaq(id) {
    if (!window.confirm('ลบ FAQ นี้?')) return;
    await api.deleteBotFaq(selectedOa, id);
    setFaqs(prev => prev.filter(f => f.id !== id));
  }

  const accuracy = stats && Number(stats.total) > 0
    ? Math.round((Number(stats.correct) / (Number(stats.correct) + Number(stats.wrong) || 1)) * 100)
    : null;

  if (!oaList.length) return <div className="empty-state-settings"><p>กรุณาเพิ่ม OA ก่อน</p></div>;

  return (
    <>
      <div className="settings-header">
        <div><h1>🤖 AI Bot — Shadow Mode</h1>
          <p>ทดสอบ Claude AI แบบ parallel ก่อน deploy จริง</p>
        </div>
      </div>

      {/* OA selector */}
      <div className="form-field" style={{ maxWidth: 320, marginBottom: 20 }}>
        <label>เลือก OA</label>
        <select className="bank-select" value={selectedOa} onChange={e => setSelectedOa(e.target.value)}>
          {oaList.map(oa => <option key={oa.id} value={oa.id}>{oa.name}</option>)}
        </select>
      </div>

      <div className="bot-layout">
        {/* Left: Settings */}
        <div className="bot-settings-col">
          <div className="dash-card">
            <div className="dash-card-title">⚙️ การตั้งค่า Bot</div>
            <div className="form-field">
              <label>Mode</label>
              <div className="bot-mode-list">
                {BOT_MODES.map(m => (
                  <label key={m.value} className={`bot-mode-item ${botMode===m.value?'active':''} ${m.disabled?'disabled':''}`}>
                    <input type="radio" value={m.value} checked={botMode===m.value}
                      onChange={() => !m.disabled && setBotMode(m.value)} disabled={m.disabled} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label>ชื่อ Bot</label>
              <input className="qr-label-input" value={botName} onChange={e => setBotName(e.target.value)} placeholder="AI Assistant" />
            </div>
            <button className="btn-primary" onClick={saveSettings} disabled={saving} style={{ width: '100%' }}>
              {saving ? '...' : '💾 บันทึกการตั้งค่า'}
            </button>
          </div>

          {/* Stats */}
          {botMode === 'shadow' && (
            <div className="dash-card" style={{ marginTop: 16 }}>
              <div className="dash-card-title" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>📊 Shadow Stats ({daysSelected} วัน)</span>
                <div style={{display:'flex',gap:4}}>
                  {[7,14,30].map(d => (
                    <button key={d} className={`days-btn${daysSelected===d?' active':''}`}
                      onClick={() => { setDaysSelected(d); setSummary(null); }}>
                      {d} วัน
                    </button>
                  ))}
                </div>
              </div>
              {statsLoading ? <div className="dash-loading">กำลังโหลด...</div> : stats ? (
                <>
                  <div className="bot-stats-grid">
                    <div className="bot-stat"><div className="bot-stat-n">{stats.total}</div><div className="bot-stat-l">ประมวลผลทั้งหมด</div></div>
                    <div className="bot-stat green"><div className="bot-stat-n">{stats.correct}</div><div className="bot-stat-l">✅ ถูก (auto)</div></div>
                    <div className="bot-stat red"><div className="bot-stat-n">{stats.wrong}</div><div className="bot-stat-l">❌ ผิด (auto)</div></div>
                    <div className="bot-stat"><div className="bot-stat-n">{accuracy !== null ? `${accuracy}%` : '—'}</div><div className="bot-stat-l">ความแม่นยำ</div></div>
                    <div className="bot-stat"><div className="bot-stat-n">{stats.auto_evaluated ?? '—'}</div><div className="bot-stat-l">🤖 เปรียบเทียบแล้ว</div></div>
                    <div className="bot-stat"><div className="bot-stat-n">{stats.unrated}</div><div className="bot-stat-l">⏳ รอ agent ตอบ</div></div>
                  </div>
                  <div style={{fontSize:11,color:'var(--text-tertiary)',marginTop:10}}>
                    ระบบเปรียบเทียบอัตโนมัติ — ไม่แสดงให้ agent เห็น
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* Right: FAQs */}
        <div className="bot-faq-col">
          <div className="dash-card">
            <div className="dash-card-title">📚 FAQ Knowledge Base</div>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>
              Claude จะตอบ auto_send เมื่อคำถามตรงกับ FAQ นี้
            </p>
            {faqs.map(f => (
              <div key={f.id} className="faq-row">
                <div className="faq-row-body">
                  <div className="faq-q">Q: {f.question}</div>
                  <div className="faq-a">A: {f.answer}</div>
                </div>
                <button className="bank-delete" onClick={() => deleteFaq(f.id)}>🗑</button>
              </div>
            ))}
            {faqs.length === 0 && <div className="bank-empty">ยังไม่มี FAQ</div>}

            {/* Add form */}
            <div className="faq-add-form">
              <div className="add-bank-field">
                <label className="add-bank-label">คำถาม</label>
                <input className="qr-label-input" value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="เช่น ฝากขั้นต่ำเท่าไหร่" />
              </div>
              <div className="add-bank-field">
                <label className="add-bank-label">คำตอบ</label>
                <textarea className="qr-content-input" value={newA} onChange={e => setNewA(e.target.value)} placeholder="เช่น ขั้นต่ำ 100 บาทครับ" rows={2} />
              </div>
              <button className="btn-primary" onClick={addFaq} disabled={adding || !newQ.trim() || !newA.trim()}
                style={{ width: '100%', padding: '9px' }}>
                {adding ? '...' : '+ เพิ่ม FAQ'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── AI Summary Report ────────────────────────── */}
      {botMode === 'shadow' && selectedOa && (
        <div className="bot-summary-wrap">
          <div className="bot-summary-header">
            <span className="bot-section-title">📊 สรุปภาพรวม AI ({daysSelected} วัน)</span>
            <button className="btn-sm" onClick={async () => {
              setSummaryLoading(true); setSummary(null);
              try { setSummary(await api.getBotShadowSummary(selectedOa, daysSelected)); }
              catch (err) { alert(err.message); }
              finally { setSummaryLoading(false); }
            }} disabled={summaryLoading}>
              {summaryLoading ? '⏳ กำลังวิเคราะห์...' : '🔄 วิเคราะห์'}
            </button>
          </div>
          {summaryLoading && <div className="dash-loading">Claude กำลังวิเคราะห์ข้อมูล...</div>}
          {summary && (() => {
            const t = Number(summary.stats?.total) || 0;
            const faqPct   = t ? Math.round(Number(summary.stats.faq_covered) / t * 100) : 0;
            const draftPct = t ? Math.round(Number(summary.stats.draft) / t * 100) : 0;
            const escPct   = t ? Math.round(Number(summary.stats.escalated) / t * 100) : 0;
            const uncPct   = Math.max(0, 100 - faqPct - draftPct - escPct);
            return (
              <>
                <div className="bot-narrative-wrap">
                  <div className="bot-narrative-label">🤖 ความเห็น AI:</div>
                  <div className="bot-narrative-text">{summary.narrative || '(ไม่มีความเห็น)'}</div>
                </div>
                {t > 0 && (
                  <div className="bot-action-breakdown">
                    <div className="breakdown-bar">
                      {faqPct   > 0 && <div className="bbar-seg bbar-faq"      style={{width:`${faqPct}%`}}   title={`FAQ ${faqPct}%`}></div>}
                      {draftPct > 0 && <div className="bbar-seg bbar-draft"    style={{width:`${draftPct}%`}} title={`Draft ${draftPct}%`}></div>}
                      {escPct   > 0 && <div className="bbar-seg bbar-escalate" style={{width:`${escPct}%`}}   title={`Agent ${escPct}%`}></div>}
                      {uncPct   > 0 && <div className="bbar-seg bbar-unclear"  style={{width:`${uncPct}%`}}   title={`ไม่เข้าใจ ${uncPct}%`}></div>}
                    </div>
                    <div className="breakdown-legend">
                      <span className="leg-faq">✅ FAQ ตอบได้ {faqPct}%</span>
                      <span className="leg-draft">📝 Draft {draftPct}%</span>
                      <span className="leg-esc">🚨 เรียก Agent {escPct}%</span>
                      <span className="leg-unc">❓ ไม่เข้าใจ {uncPct}%</span>
                    </div>
                  </div>
                )}
                {summary.topics?.length > 0 && (
                  <div className="bot-topics-wrap">
                    <div className="bot-topics-label">หัวข้อที่ลูกค้าถามบ่อย</div>
                    <div className="bot-topics-list">
                      {summary.topics.map((tp, i) => (
                        <div key={i} className="bot-topic-row">
                          <span className="topic-rank">#{i+1}</span>
                          <span className="topic-name">{tp.name}</span>
                          <span className="topic-count">{tp.count} ครั้ง</span>
                          {tp.faq_possible && <span className="topic-faq-badge">FAQ</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Review Section ───────────────────────────── */}
      {botMode === 'shadow' && selectedOa && (
        <ReviewSection oaId={selectedOa} days={daysSelected} />
      )}
    </>
  );
}

// ─── Review Section ───────────────────────────────────
function ReviewSection({ oaId, days = 7 }) {
  const [logs, setLogs]       = useState([]);
  const [filter, setFilter]   = useState('unrated');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!oaId) return;
    setLoading(true);
    api.getBotShadowLogs(oaId, { rating: filter, days, limit: 50 })
      .then(setLogs).catch(console.error).finally(() => setLoading(false));
  }, [oaId, filter, days]);

  const FILTERS = [
    { key: 'unrated', label: '🆕 ใหม่' },
    { key: 'wrong',   label: '❌ ผิด'  },
    { key: 'correct', label: '✅ ถูก'  },
    { key: 'all',     label: 'ทั้งหมด' },
  ];

  return (
    <div style={{ marginTop: 24 }}>
      <div className="dash-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="dash-card-title">🔍 Claude ตอบอะไร — 7 วันที่ผ่านมา</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {FILTERS.map(({ key, label }) => (
              <button key={key}
                className={`dash-period ${filter === key ? 'active' : ''}`}
                onClick={() => setFilter(key)}
              >{label}</button>
            ))}
          </div>
        </div>

        {loading && <div className="dash-loading">กำลังโหลด...</div>}

        {!loading && logs.length === 0 && (
          <div className="dash-empty">ยังไม่มีข้อมูล</div>
        )}

        <div className="review-list">
          {logs.map(log => (
            <div key={log.id} className={`review-row review-${log.agent_rating}`}>
              <div className="review-badge-col">
                <span className={`review-badge ${log.agent_rating}`}>
                  {log.agent_rating === 'correct' ? '✅' : log.agent_rating === 'wrong' ? '❌' : '🆕'}
                </span>
                <span className="review-date">
                  {new Date(log.created_at).toLocaleDateString('th-TH', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                </span>
              </div>
              <div className="review-messages">
                <div className="review-msg customer">
                  <div className="review-msg-label">
                    👤 ลูกค้าถาม
                    {log.debounced_messages && (
                      <span className="review-debounce-badge">
                        🔗 {log.debounced_messages.split('\n').length} ข้อความ
                      </span>
                    )}
                  </div>
                  <div className="review-msg-text" style={{ whiteSpace: 'pre-wrap' }}>
                    {log.customer_message || '—'}
                  </div>
                </div>
                <div className="review-msg claude">
                  <div className="review-msg-label">🤖 Claude ตอบ <span className={`review-action-tag ${log.claude_action}`}>{log.claude_action}</span></div>
                  <div className="review-msg-text">{log.claude_action === 'unclear' ? '—' : (log.claude_message || '—')}</div>
                </div>
                {/* แสดง agent reply เฉพาะเมื่อดู wrong/correct/all */}
                {filter !== 'unrated' && (
                  <div className="review-msg agent">
                    <div className="review-msg-label">👨‍💼 Agent ตอบ</div>
                    <div className="review-msg-text">{log.agent_message || '(ยังไม่มีการตอบ)'}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Executive AI Tab ────────────────────────────────
// ─── Profile Tab (any agent) ──────────────────────────
function ProfileTab() {
  const { agent } = useAuth();
  const [chatId, setChatId]           = useState('');
  const [hasChatId, setHasChatId]     = useState(false);
  const [botToken, setBotToken]       = useState('');
  const [hasBotToken, setHasBotToken] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [status, setStatus]           = useState('');

  useEffect(() => {
    api.getAgentProfile().then(data => {
      setChatId(data.telegram_chat_id || '');
      setHasChatId(!!data.telegram_chat_id);
      setBotToken('');  // never pre-fill secrets
      setHasBotToken(!!data.telegram_bot_token_set);
    }).catch(console.error);
  }, []);

  async function save() {
    setSaving(true); setStatus('');
    try {
      // Only send fields that have a value — empty bot token keeps existing
      const payload = { telegram_chat_id: chatId.trim() };
      if (botToken.trim()) payload.telegram_bot_token = botToken.trim();
      await api.updateAgentProfile(payload);
      setHasChatId(!!chatId.trim());
      if (botToken.trim()) setHasBotToken(true);
      setBotToken('');  // clear input after save
      setStatus('saved');
    } catch (err) { setStatus('error:' + err.message); }
    finally { setSaving(false); setTimeout(() => setStatus(''), 4000); }
  }

  async function clearBotToken() {
    if (!window.confirm('ลบ Bot Token? — จะกลับไปใช้ bot กลางของระบบ')) return;
    setSaving(true);
    try {
      await api.updateAgentProfile({ telegram_bot_token: '' });
      setHasBotToken(false);
      setStatus('saved');
    } catch (err) { setStatus('error:' + err.message); }
    finally { setSaving(false); setTimeout(() => setStatus(''), 4000); }
  }

  const badge = (
    <span style={{ fontSize: 10, fontWeight: 500, color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>
      ✓ ตั้งค่าไว้แล้ว
    </span>
  );

  return (
    <>
      <div className="settings-header">
        <div>
          <h1>👤 โปรไฟล์ของฉัน</h1>
          <p>ตั้งค่าการแจ้งเตือนส่วนตัวผ่าน Telegram</p>
        </div>
      </div>

      <div className="dash-card" style={{ maxWidth: 520 }}>
        <div className="dash-card-title">🔔 แจ้งเตือน Telegram ของฉัน</div>

        {/* Chat ID */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
          Telegram Chat ID {hasChatId && badge}
        </label>
        <input className="form-input" style={{ width: '100%' }}
          placeholder="เช่น 123456789 หรือ -1001234567890 (group)"
          value={chatId} onChange={e => setChatId(e.target.value)}
          autoComplete="off" />
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, marginBottom: 16 }}>
          💡 ส่ง <code>/start</code> ให้ Telegram bot เพื่อรับ Chat ID
        </div>

        {/* Bot Token */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
          Bot Token (Optional) {hasBotToken && badge}
        </label>
        <input type="password" className="form-input" style={{ width: '100%' }}
          placeholder={hasBotToken ? '(เว้นว่างเพื่อใช้ token เดิม)' : '123456:ABC-... จาก @BotFather'}
          value={botToken} onChange={e => setBotToken(e.target.value)}
          autoComplete="off" />
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
          💡 ถ้าใส่ — ระบบจะส่งแจ้งเตือนผ่าน bot ของคุณ
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
          ถ้าไม่ใส่ — ใช้ bot กลางของระบบ (ถ้า admin ตั้งค่าไว้)
        </div>
        {hasBotToken && (
          <button type="button" onClick={clearBotToken}
            style={{ marginTop: 8, padding: '4px 10px', fontSize: 11, background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            ลบ Bot Token
          </button>
        )}

        <button className="btn-primary" onClick={save} disabled={saving}
          style={{ marginTop: 12, width: '100%' }}>
          {saving ? 'กำลังบันทึก...' : status === 'saved' ? '✅ บันทึกแล้ว' : 'บันทึก'}
        </button>
        {status.startsWith('error') && (
          <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{status.replace('error:', '')}</div>
        )}
      </div>

      <div className="dash-card" style={{ maxWidth: 520, marginTop: 16 }}>
        <div className="dash-card-title">ข้อมูลส่วนตัว</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <div><strong>ชื่อ:</strong> {agent?.name}</div>
          <div><strong>Email:</strong> {agent?.email}</div>
          <div><strong>Role:</strong> {agent?.role}</div>
        </div>
      </div>
    </>
  );
}

function ExecutiveAiTab() {
  // Sensitive fields start empty — backend returns *_set boolean, never the actual value
  const [form, setForm]     = useState({
    openrouter_api_key: '', telegram_bot_token: '', telegram_allowed_ids: '',
    telegram_notify_enabled: '0', telegram_notify_chat_id: '',
  });
  const [flags, setFlags]   = useState({ openrouter_api_key_set: false, telegram_bot_token_set: false });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    api.getSystemConfig().then(cfg => {
      setForm({
        openrouter_api_key:       '',           // never pre-fill secrets
        telegram_bot_token:       '',
        telegram_allowed_ids:     cfg.telegram_allowed_ids || '',
        telegram_notify_enabled:  cfg.telegram_notify_enabled || '0',
        telegram_notify_chat_id:  cfg.telegram_notify_chat_id || '',
      });
      setFlags({
        openrouter_api_key_set: !!cfg.openrouter_api_key_set,
        telegram_bot_token_set: !!cfg.telegram_bot_token_set,
      });
    }).catch(console.error);
  }, []);

  async function save() {
    setSaving(true); setStatus('');
    try {
      // Only send fields that have a value — empty secret fields = keep existing
      const payload = {};
      if (form.openrouter_api_key.trim())   payload.openrouter_api_key   = form.openrouter_api_key.trim();
      if (form.telegram_bot_token.trim())   payload.telegram_bot_token   = form.telegram_bot_token.trim();
      payload.telegram_allowed_ids    = form.telegram_allowed_ids;
      payload.telegram_notify_enabled = form.telegram_notify_enabled;
      payload.telegram_notify_chat_id = form.telegram_notify_chat_id;
      await api.updateSystemConfig(payload);
      // Clear secret fields after save and update flags
      setForm(f => ({ ...f, openrouter_api_key: '', telegram_bot_token: '' }));
      setFlags(fl => ({
        openrouter_api_key_set: fl.openrouter_api_key_set || !!payload.openrouter_api_key,
        telegram_bot_token_set: fl.telegram_bot_token_set || !!payload.telegram_bot_token,
      }));
      setStatus('saved');
    } catch (err) { setStatus('error:' + err.message); }
    finally { setSaving(false); setTimeout(() => setStatus(''), 4000); }
  }

  const field = (key, label, placeholder, hint, isSensitive = false) => {
    const isSet = flags[`${key}_set`];
    return (
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
          {label}
          {isSensitive && isSet && (
            <span style={{ fontSize: 10, fontWeight: 500, color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>
              ✓ ตั้งค่าไว้แล้ว
            </span>
          )}
        </label>
        <input
          type={isSensitive ? 'password' : 'text'}
          className="form-input" style={{ width: '100%' }}
          placeholder={isSensitive && isSet ? '(เว้นว่างเพื่อใช้ค่าเดิม)' : placeholder}
          value={form[key] || ''}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          autoComplete="off"
        />
        {hint && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{hint}</div>}
      </div>
    );
  };

  return (
    <div className="settings-section">
      <div className="settings-header">
        <div>
          <h1>📲 Executive AI</h1>
          <p>ผู้บริหารถามข้อมูลจากระบบแชทได้ผ่าน Telegram — AI จะดึงข้อมูลและตอบกลับอัตโนมัติ</p>
        </div>
      </div>
      <div className="dash-card" style={{ maxWidth: 520 }}>
        <div className="dash-card-title">⚙️ ตั้งค่าการเชื่อมต่อ</div>
        {field('openrouter_api_key', 'OpenRouter API Key', 'sk-or-v1-...', 'ถ้าใส่จะ override ค่าใน .env — เว้นว่างเพื่อใช้ค่าเดิม', true)}
        {field('telegram_bot_token', 'Telegram Bot Token', '123456:ABC-...', 'สร้าง bot ผ่าน @BotFather — เว้นว่างเพื่อใช้ค่าเดิม', true)}
        {field('telegram_allowed_ids', 'Chat IDs ที่อนุญาต (คั่นด้วย ,)', '-1001234567890,123456789',
          'Group ID ขึ้นต้นด้วย "-" (เช่น -1001234567890) — อย่าลืมเครื่องหมายลบ! ส่ง /start ให้ bot เพื่อดู Chat ID')}
        <button className="btn-primary" onClick={save} disabled={saving} style={{ marginTop: 4 }}>
          {saving ? 'กำลังบันทึก...' : status === 'saved' ? '✅ บันทึกแล้ว — bot จะ restart อัตโนมัติ' : 'บันทึกและเริ่ม Bot'}
        </button>
        {status.startsWith('error') && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{status}</div>}
      </div>

      <div className="dash-card" style={{ maxWidth: 520, marginTop: 16 }}>
        <div className="dash-card-title">🔔 แจ้งเตือนเมื่อลูกค้าทัก</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox"
            checked={form.telegram_notify_enabled === '1'}
            onChange={e => setForm(f => ({ ...f, telegram_notify_enabled: e.target.checked ? '1' : '0' }))} />
          <span style={{ fontSize: 13 }}>เปิดใช้งานแจ้งเตือนผ่าน Telegram เมื่อมีข้อความเข้า</span>
        </label>
        {field('telegram_notify_chat_id', 'Chat ID ที่รับแจ้งเตือน',
          '-1001234567890 หรือ 123456789',
          'group หรือ personal chat (group ขึ้นต้นด้วย "-") — ใช้ bot เดียวกับ Executive AI')}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
          💡 ระบบจะรอ 5 วินาทีแล้วรวมหลายข้อความจากลูกค้าคนเดียวกันเป็นแจ้งเตือนเดียว
        </div>
      </div>

      <div className="dash-card" style={{ maxWidth: 520, marginTop: 16 }}>
        <div className="dash-card-title">💬 วิธีใช้งาน</div>
        <ol style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 20, color: 'var(--text-secondary)', margin: 0 }}>
          <li>สร้าง bot ผ่าน <strong>@BotFather</strong> ใน Telegram → รับ token</li>
          <li>ใส่ token + Chat ID ของผู้บริหารด้านบน → กด <strong>บันทึก</strong></li>
          <li>เปิด Telegram → ส่งข้อความให้ bot ได้เลย เช่น</li>
        </ol>
        <div style={{ background: 'var(--surface)', borderRadius: 6, padding: '8px 12px', marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          <div>📝 <em>"สรุปยอดสัปดาห์นี้"</em></div>
          <div>📝 <em>"agent คนไหน active มากที่สุด"</em></div>
          <div>📝 <em>"ลูกค้ามีปัญหาเรื่องอะไรบ้าง"</em></div>
          <div>📝 <em>"AI Shadow Mode แม่นยำแค่ไหน"</em></div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Settings Page ───────────────────────────────
export default function SettingsPage() {
  const navigate = useNavigate();
  const { agent, logout } = useAuth();
  const [oaList, setOaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [tab, setTab] = useState('oa');
  const [broadcastOa, setBroadcastOa] = useState(null);
  const [richMenuOa, setRichMenuOa] = useState(null);

  useEffect(() => {
    api.getOAAccounts()
      .then(setOaList)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(form) {
    if (editTarget) {
      const updated = await api.updateOAAccount(editTarget.id, form);
      setOaList(prev => prev.map(o => o.id === updated.id ? updated : o));
    } else {
      const created = await api.createOAAccount(form);
      setOaList(prev => [...prev, created]);
    }
    setShowForm(false);
    setEditTarget(null);
  }

  async function handleDelete(id) {
    await api.deleteOAAccount(id);
    setOaList(prev => prev.filter(o => o.id !== id));
  }

  async function handleToggle(oa) {
    const updated = await api.updateOAAccount(oa.id, { is_active: !oa.is_active });
    setOaList(prev => prev.map(o => o.id === updated.id ? updated : o));
  }

  function handleEdit(oa) {
    setEditTarget(oa);
    setShowForm(true);
  }

  return (
    <div className="settings-layout">
      {/* Sidebar */}
      <aside className="settings-sidebar">
        <div className="settings-logo">
          <MergeChatLogo size={32} />
          <span><span>Merge</span><span style={{color:'#FF6347', fontWeight:700}}>Chat</span></span>
        </div>
        <nav className="settings-nav">
          <button className="nav-item" onClick={() => navigate('/')}>
            💬 กล่องข้อความ
          </button>
          <button className={`nav-item ${tab === 'oa' ? 'active' : ''}`} onClick={() => setTab('oa')}>
            ⚙️ จัดการ OA
          </button>
          <button className={`nav-item ${tab === 'webhook' ? 'active' : ''}`} onClick={() => setTab('webhook')}>
            🔗 Webhook URLs
          </button>
          <button className={`nav-item ${tab === 'quick-replies' ? 'active' : ''}`} onClick={() => setTab('quick-replies')}>
            ⚡ Quick Replies
          </button>
          <button className={`nav-item ${tab === 'tags' ? 'active' : ''}`} onClick={() => setTab('tags')}>
            🏷 จัดการ Tags
          </button>
          <button className={`nav-item ${tab === 'profile' ? 'active' : ''}`} onClick={() => setTab('profile')}>
            👤 โปรไฟล์ของฉัน
          </button>
          {agent?.role === 'admin' && (
            <button className={`nav-item ${tab === 'agents' ? 'active' : ''}`} onClick={() => setTab('agents')}>
              👥 จัดการ Agent
            </button>
          )}
          {agent?.role === 'admin' && (
            <button className={`nav-item ${tab === 'permissions' ? 'active' : ''}`} onClick={() => setTab('permissions')}>
              🔐 จัดการสิทธิ์
            </button>
          )}
          {agent?.role === 'admin' && (
            <button className={`nav-item ${tab === 'bot' ? 'active' : ''}`} onClick={() => setTab('bot')}>
              🤖 AI Bot
            </button>
          )}
          {agent?.role === 'admin' && (
            <button className={`nav-item ${tab === 'executive-ai' ? 'active' : ''}`} onClick={() => setTab('executive-ai')}>
              📲 Executive AI
            </button>
          )}
        </nav>
        <div className="settings-agent">
          <div className="agent-info">
            <div className="agent-avatar">{agent?.name?.slice(0, 2)}</div>
            <div>
              <div className="agent-name">{agent?.name}</div>
              <div className="agent-role">{agent?.role}</div>
            </div>
          </div>
          <button className="btn-logout-sm" onClick={logout}>ออก</button>
        </div>
        {process.env.REACT_APP_VERSION && (
          <div className="settings-version">v {process.env.REACT_APP_VERSION}</div>
        )}
      </aside>

      {/* Content */}
      <main className="settings-main">
        {tab === 'oa' && (
          <>
            <div className="settings-header">
              <div>
                <h1>จัดการ Line OA</h1>
                <p>เชื่อมต่อและตั้งชื่อ Line OA ของคุณ ({oaList.length} บัญชี)</p>
              </div>
              <button className="btn-primary" onClick={() => { setEditTarget(null); setShowForm(true); }}>
                + เพิ่ม OA ใหม่
              </button>
            </div>

            {loading ? (
              <div className="loading-state">กำลังโหลด...</div>
            ) : oaList.length === 0 ? (
              <div className="empty-state-settings">
                <div className="empty-icon">📱</div>
                <p>ยังไม่มี OA ที่เชื่อมต่อ</p>
                <button className="btn-primary" onClick={() => setShowForm(true)}>+ เพิ่ม OA แรก</button>
              </div>
            ) : (
              <div className="oa-list">
                {oaList.map(oa => (
                  <OACard key={oa.id} oa={oa}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    onBroadcast={setBroadcastOa}
                    onRichMenu={setRichMenuOa}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'webhook' && (
          <>
            <div className="settings-header">
              <div>
                <h1>Webhook URLs</h1>
                <p>คัดลอก URL ไปวางใน Line Developers Console</p>
              </div>
            </div>
            <div className="webhook-info">
              <div className="info-box">
                <h4>วิธีตั้งค่า Webhook</h4>
                <ol>
                  <li>ไปที่ <strong>developers.line.biz</strong></li>
                  <li>เลือก OA → Messaging API</li>
                  <li>วาง URL ด้านล่างใน <strong>Webhook URL</strong></li>
                  <li>เปิด <strong>Use webhook</strong> → ON</li>
                  <li>กด <strong>Verify</strong></li>
                </ol>
              </div>
            </div>
            <div className="oa-list">
              {oaList.map(oa => (
                <div key={oa.id} className="webhook-card">
                  <div className="oa-color-badge" style={{ background: oa.color }} />
                  <div className="webhook-info-row">
                    <div className="oa-card-name">{oa.name}</div>
                    <div className="webhook-url">
                      <code>{window.location.origin.replace('3001', '3000')}/api/webhook/{oa.channel_id}</code>
                      <button className="btn-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin.replace('3001', '3000')}/api/webhook/${oa.channel_id}`);
                          alert('คัดลอกแล้ว!');
                        }}>
                        คัดลอก
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {oaList.length === 0 && (
                <div className="empty-state-settings">
                  <p>ยังไม่มี OA — เพิ่ม OA ก่อนในแท็บ "จัดการ OA"</p>
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'quick-replies' && <QuickRepliesTab />}
        {tab === 'tags' && <TagsTab />}
        {tab === 'profile' && <ProfileTab />}
        {tab === 'agents' && agent?.role === 'admin' && <AgentsTab />}
        {tab === 'permissions' && agent?.role === 'admin' && <PermissionsTab />}
        {tab === 'bot' && agent?.role === 'admin' && <BotTab oaList={oaList} />}
        {tab === 'executive-ai' && agent?.role === 'admin' && <ExecutiveAiTab />}
      </main>

      {showForm && (
        <OAForm
          initial={editTarget}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}
      {broadcastOa && (
        <BroadcastModal oa={broadcastOa} onClose={() => setBroadcastOa(null)} />
      )}
      {richMenuOa && (
        <RichMenuModal oa={richMenuOa} onClose={() => setRichMenuOa(null)} />
      )}
    </div>
  );
}
